// Top-level React error boundary. If any page throws during render,
// shows a friendly fallback with a reload CTA instead of a blank
// screen. Sentry captures the error via the global handler in
// services/sentry.ts.

import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  err: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { err: null };

  static getDerivedStateFromError(err: Error): State {
    return { err };
  }

  override componentDidCatch(err: Error): void {
    // Sentry's React integration wires window.onerror; we also log
    // locally for the dev console.
    // eslint-disable-next-line no-console
    console.error('Unhandled error in render tree:', err);
  }

  reset = () => {
    this.setState({ err: null });
  };

  reload = () => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  override render(): ReactNode {
    if (!this.state.err) return this.props.children;
    return (
      <div
        role="alert"
        style={{
          maxWidth: 560,
          margin: '40px auto',
          padding: 24,
          background: '#ffffff',
          borderRadius: 18,
          boxShadow: '0 12px 30px rgba(15, 23, 42, 0.08)',
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        <h1 style={{ fontSize: 28, marginTop: 0, color: '#0f4c75' }}>Something went sideways.</h1>
        <p style={{ fontSize: 18, lineHeight: 1.5, color: '#475569' }}>
          MedReview hit an unexpected error. Your data is safe. Tap reload to try again, and if it
          keeps happening, email{' '}
          <a href="mailto:support@otchealthmart.com">support@otchealthmart.com</a>.
        </p>
        <p
          style={{
            fontSize: 13,
            color: '#94a3b8',
            background: '#f1f5f9',
            padding: 12,
            borderRadius: 8,
            wordBreak: 'break-word',
          }}
        >
          {this.state.err.message}
        </p>
        <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
          <button
            type="button"
            onClick={this.reload}
            style={{
              background: '#0f4c75',
              color: '#ffffff',
              border: 0,
              fontWeight: 700,
              fontSize: 18,
              padding: '14px 28px',
              borderRadius: 999,
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
          <button
            type="button"
            onClick={this.reset}
            style={{
              background: '#ffffff',
              color: '#0f4c75',
              border: '1.5px solid #0f4c75',
              fontWeight: 700,
              fontSize: 18,
              padding: '14px 24px',
              borderRadius: 999,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}
